import React, { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { useCreateService } from '../../hooks/useServices'

interface AddServiceDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Called after service is successfully created with the new service ID */
  onCreated?: (serviceId: string) => void
}

/**
 * Simple dialog for admins to create new masonry services.
 * Used when a project's service type isn't in the predefined list.
 *
 * @see servicesApi.ts - createService() generates slug from name
 * @see supabase/migrations/20251204_create_services_table.sql - Services table
 */
export function AddServiceDialog({
  open,
  onOpenChange,
  onCreated,
}: AddServiceDialogProps) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')

  const createService = useCreateService()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!name.trim()) return

    try {
      // Note: API expects 'title' field to match existing services table schema
      const newService = await createService.mutateAsync({
        title: name.trim(),
        description: description.trim() || undefined,
      })

      // Reset form
      setName('')
      setDescription('')

      // Notify parent of new service
      onCreated?.(newService.id)

      // Close dialog
      onOpenChange(false)
    } catch (err) {
      // Error is handled by mutation's error state
      console.error('Failed to create service:', err)
    }
  }

  const handleClose = () => {
    // Reset form on close
    setName('')
    setDescription('')
    onOpenChange(false)
  }

  // Generate slug preview from name
  const slugPreview = name
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add New Service</DialogTitle>
          <DialogDescription>
            Create a new masonry service type for categorizing galleries.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="service-name">Service Name *</Label>
            <Input
              id="service-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Jewelstone, Flagstone"
              required
              autoFocus
            />
            {slugPreview && (
              <p className="text-xs text-muted-foreground mt-1">
                Slug: {slugPreview}
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="service-description">Description (optional)</Label>
            <Textarea
              id="service-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of this service type..."
              rows={2}
            />
          </div>

          {createService.isError && (
            <p className="text-sm text-destructive">
              Failed to create service. Please try again.
            </p>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={createService.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!name.trim() || createService.isPending}
            >
              {createService.isPending ? 'Creating...' : 'Add Service'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default AddServiceDialog
